import PropTypes from 'prop-types';
import React from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import Field from '../../constants/editable-field-tags';
import { Translate } from '@wsb/vnext-widget-content-translate';
import {
  NumberFormatter,
  OlsConfigActions,
  OlsConfigStore
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { YotpoActions } from '@wsb/guac-widget-shared/lib/common/ols-core/shared-bundle';
import ProductGroupActions from './utils/ProductGroupActions';
import ProductGroupStore from './utils/ProductGroupStore';
import dataAids from '../../constants/dataAids';
import { BaseContainerContext } from './BaseContainerContext';

class BaseContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.getStoreState();
  }

  getContextValue() {
    return {
      translate: Translate(this.props.staticContent)
    };
  }

  getStoreState() {
    const { products, error, fetching, productDataWasLoaded } = ProductGroupStore.getState();
    const loadingConfigError = OlsConfigStore.getState().error;

    return {
      products,
      error,
      fetching,
      productDataWasLoaded,
      loadingConfigError,
      provisioned: OlsConfigStore.isProvisioned(),
      provisionInProgress: OlsConfigStore.isProvisionInProgress(),
      provisionTried: OlsConfigStore.provisionTried()
    };
  }

  componentDidMount() {
    this.initialize();
  }

  componentDidUpdate(prevProps) {
    const { app_key: appKey } = OlsConfigStore.getState().yotpo_config || {};

    if (appKey) {
      window.setTimeout(() => {
        YotpoActions.loadYotpo();
      }, 0);
    }

    // BEAR-488: New sites start with null websiteId
    if (prevProps.websiteId !== this.props.websiteId) {
      OlsConfigActions.clearError();
      OlsConfigActions.setConfig(this.props);
      OlsConfigActions.loadConfig();
    }
  }

  componentWillUnmount() {
    ProductGroupStore.removeListener('change', this.onStoreChange);
    OlsConfigStore.removeListener('change', this.onOlsConfigStoreChange);
    window.removeEventListener('olsProductsUpdated', this.reloadProducts);
  }

  onOlsConfigStoreChange = () => {
    const olsConfigStoreState = OlsConfigStore.getState();
    NumberFormatter.setNumberFormat(olsConfigStoreState.number.format);
    NumberFormatter.setCurrencyFormat(olsConfigStoreState.currency.format);
    this.setState(this.getStoreState(), this.loadProductGroupData);
  };

  onStoreChange = () => {
    this.setState(this.getStoreState());
  };

  initialize() {
    this.onStoreChange();
    OlsConfigStore.addListener('change', this.onOlsConfigStoreChange);
    ProductGroupStore.addListener('change', this.onStoreChange);
    window.addEventListener('olsProductsUpdated', this.reloadProducts);
    OlsConfigActions.setConfig(this.props);

    OlsConfigActions.loadConfig();
    this.loadProductGroupData();
  }

  loadProductGroupData() {
    if (this.state.provisioned && !this.props.useStaticData) {
      setTimeout(ProductGroupActions.loadProductGroup, 0);
    }
  }

  reloadProducts = () => {
    setTimeout(ProductGroupActions.loadProductGroup(1, true), 0);
  };

  get willShowFetching() {
    if (!this.state.productDataWasLoaded && this.state.provisioned) return true;

    return (
      !this.state.provisionTried && (!this.state.provisioned || this.state.provisionInProgress)
    );
  }

  get willUseSampleData() {
    const { renderMode, hasNotCreatedAProduct } = this.props;
    const { productDataWasLoaded, provisioned, products } = this.state;

    const hasNotCreatedProducts =
      ['ADD', 'DISPLAY', 'SAMPLE', 'EDIT'].includes(renderMode) && hasNotCreatedAProduct;

    const hasNoProducts = productDataWasLoaded && !products.length;
    const hasNoDataAvailable =
      ['ADD', 'DISPLAY', 'SAMPLE'].includes(renderMode) && (!provisioned || hasNoProducts);

    return hasNoDataAvailable || hasNotCreatedProducts;
  }

  render() {
    const {
      category,
      isInternalPage,
      order,
      section,
      sectionTitle,
      sampleProductsData,
      Component
    } = this.props;
    const {
      error,
      productDataWasLoaded,
      provisioned,
      provisionTried,
      loadingConfigError,
      products
    } = this.state;

    const { Container, Heading, Block } = UX2.Element;

    return (
      <BaseContainerContext.Provider value={ this.getContextValue() }>
        <Container
          data-aid={ dataAids.PRODUCT_GROUP_RENDERED }
          category={ category }
          section={ section }
          data-container-id='ols-featured-products'
        >
          { sectionTitle && (
            <Heading
              children={ sectionTitle }
              data-aid={ dataAids.PRODUCT_GROUP_TITLE_RENDERED }
              data-route={ Field.TITLE }
              isInternalPage={ isInternalPage }
              order={ order }
            />
          ) }
          <Block>
            <Component
              { ...this.props }
              products={ this.willUseSampleData ? sampleProductsData.products : products }
              productDataWasLoaded={ productDataWasLoaded }
              willUseSampleData={ this.willUseSampleData }
              fetching={ this.willShowFetching }
              renderPrices={ true }
              error={ error }
              configError={ loadingConfigError }
              provisionHasError={ !provisioned && provisionTried }
            />
          </Block>
        </Container>
      </BaseContainerContext.Provider>
    );
  }
}

BaseContainer.propTypes = {
  sectionTitle: PropTypes.string,
  renderMode: PropTypes.string,
  category: PropTypes.string,
  section: PropTypes.string,
  isReseller: PropTypes.bool,
  order: PropTypes.any,
  hasNotCreatedAProduct: PropTypes.bool,
  sampleProductsData: PropTypes.object,
  isInternalPage: PropTypes.bool,
  staticContent: PropTypes.object,
  shopRoute: PropTypes.string,
  domainName: PropTypes.string,
  useStaticData: PropTypes.bool,
  Component: PropTypes.any,
  websiteId: PropTypes.string
};

export default BaseContainer;
